import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Play, TrendingUp, Clock, Users, Crown, Coins, Headphones, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { SongCard } from "@/components/SongCard";
import { Waveform, coverFor } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4 px-1">
        <h2 className="text-xl font-bold font-display flex items-center gap-2">{Icon && <Icon size={20} className="text-purple-400" />} {title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

const GENRES = ["Synthwave", "Hip-Hop", "Pop", "EDM", "Lo-Fi", "Cinematic", "Rock", "R&B"];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [creators, setCreators] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/songs", { params: { section: "trending", limit: 15 } }).then((r) => setTrending(r.data.songs)).catch(() => {});
    api.get("/songs", { params: { section: "recent", limit: 15 } }).then((r) => setRecent(r.data.songs)).catch(() => {});
    api.get("/songs", { params: { section: "featured", limit: 10 } }).then((r) => setFeatured(r.data.songs)).catch(() => {});
    api.get("/creators/popular").then((r) => setCreators(r.data.creators)).catch(() => {});
  }, []);

  const allSongs = [...trending, ...recent];

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6">
      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden jx-radial-bloom border border-white/10 p-8 sm:p-12 mb-10">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1669937401545-c10155e96124?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" className="w-full h-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/80 to-transparent" />
        </div>
        <div className="relative z-10 max-w-xl">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-400 mb-3">AI Music Studio & Streaming</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-display leading-none">Make a hit in <span className="jx-text-gradient">seconds</span></h1>
          <p className="text-slate-300 mt-4 max-w-md">Generate original songs with AI, publish to the world, and earn every time someone listens. Your Voice. Your Sound. Your World.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => navigate("/studio")} data-testid="hero-create-btn" className="jx-cta text-white font-semibold px-6 py-3 rounded-xl flex items-center gap-2">
              <Sparkles size={18} /> Create Music
            </button>
            <Link to="/explore" className="border border-white/15 hover:border-purple-500/50 text-white font-semibold px-6 py-3 rounded-xl flex items-center gap-2 transition-colors">
              <Headphones size={18} /> Explore
            </Link>
          </div>
          <Waveform bars={16} className="h-8 mt-8 opacity-80" />
        </div>
      </div>

      {/* Genre chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8">
        {GENRES.map((g) => (
          <button key={g} onClick={() => navigate(`/explore?genre=${g.toLowerCase()}`)}
            className="shrink-0 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300 hover:border-purple-500/40 transition-colors">{g}</button>
        ))}
      </div>

      {trending.length > 0 && (
        <Section title="Trending Now" icon={TrendingUp} action={<Link to="/explore" className="text-sm text-purple-400 flex items-center gap-1">All <ArrowRight size={14} /></Link>}>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {trending.map((s) => <SongCard key={s.id} song={s} list={allSongs} />)}
          </div>
        </Section>
      )}

      {creators.length > 0 && (
        <Section title="Popular Creators" icon={Users}>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {creators.map((c) => (
              <Link key={c.id} to={`/u/${c.username}`} className="jx-card rounded-2xl p-4 w-40 shrink-0 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 grid place-items-center text-2xl font-bold overflow-hidden mb-3">
                  {c.avatar_url ? <img src={c.avatar_url} className="w-full h-full object-cover" alt="" /> : c.display_name?.[0]}
                </div>
                <p className="font-semibold text-sm truncate">{c.display_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.followers_count} followers</p>
                <p className="text-[11px] text-purple-400 mt-1">{c.song_count} tracks</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {recent.length > 0 && (
        <Section title="New Releases" icon={Clock}>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {recent.map((s) => <SongCard key={s.id} song={s} list={allSongs} />)}
          </div>
        </Section>
      )}

      {featured.length > 0 && (
        <Section title="Featured" icon={Sparkles}>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {featured.map((s) => <SongCard key={s.id} song={s} list={allSongs} />)}
          </div>
        </Section>
      )}

      {allSongs.length === 0 && (
        <div className="text-center py-16 jx-card rounded-3xl">
          <Sparkles className="mx-auto text-purple-400 mb-3" size={40} />
          <p className="font-semibold text-lg">No music yet — be the first!</p>
          <p className="text-slate-400 text-sm mt-1">Subscribe to a creator plan and generate your first AI track.</p>
          <button onClick={() => navigate("/studio")} className="jx-cta text-white font-semibold px-6 py-3 rounded-xl mt-4">Create Music</button>
        </div>
      )}

      {/* Value props */}
      <div className="grid md:grid-cols-3 gap-4 my-10">
        {[
          { icon: Crown, title: "Creator Earnings", desc: "Publish songs and earn from every eligible play. Rates are set by admins and paid from a funded reward budget." },
          { icon: Coins, title: "Listening Rewards", desc: "Free members earn wallet credit for qualifying listens — real sessions only, with anti-fraud limits." },
          { icon: Users, title: "Tip a Creator", desc: "Support artists directly from your wallet. Instant, atomic, and tracked in your ledger." },
        ].map((f) => (
          <div key={f.title} className="jx-card rounded-2xl p-6">
            <div className="w-11 h-11 rounded-xl jx-cta grid place-items-center mb-3"><f.icon size={20} className="text-white" /></div>
            <h3 className="font-bold font-display">{f.title}</h3>
            <p className="text-sm text-slate-400 mt-1">{f.desc}</p>
          </div>
        ))}
      </div>

      {/* Plans teaser */}
      <div className="jx-radial-bloom border border-white/10 rounded-3xl p-8 text-center mb-10">
        <h2 className="text-2xl font-bold font-display">Unlock the AI Studio</h2>
        <p className="text-slate-400 mt-2">Listening is always free. Upgrade to generate unlimited AI music.</p>
        <Link to="/plans" className="inline-flex items-center gap-2 jx-cta text-white font-semibold px-6 py-3 rounded-xl mt-5">View Plans <ArrowRight size={16} /></Link>
      </div>

      <footer className="border-t border-white/10 pt-8 pb-4 text-sm text-slate-500">
        <div className="flex flex-wrap gap-x-8 gap-y-2 mb-4">
          <span className="text-slate-400 font-semibold">JixxeoX</span>
          <a className="hover:text-slate-300" href="#">Terms</a>
          <a className="hover:text-slate-300" href="#">Privacy</a>
          <a className="hover:text-slate-300" href="#">Copyright / DMCA</a>
          <a className="hover:text-slate-300" href="#">Support</a>
        </div>
        <p className="text-xs">Music generated by AI providers is subject to provider terms and licensing. Do not upload or clone voices without authorization. © 2026 JixxeoX.</p>
      </footer>
    </div>
  );
}
